/**
 * Context-aware API client hook.
 *
 * Returns a scoped API wrapper that automatically injects the `currentcrmorgid`
 * header into every request, using the crmOrgId from the closest DefaultEnvContext
 * provider. This ensures that concurrent requests from different org contexts
 * (e.g. GlobalDefaultEnvProvider vs ScopedEnvProvider) carry the correct header.
 *
 * Callers can still override the header per-request via explicit config — the
 * per-request value takes precedence over the context default.
 *
 * Safe to use outside a DefaultEnvContext provider (e.g. AccountSelectionProvider,
 * OAuth redirects) — the header is simply omitted when no context is available.
 */
import { useContext, useMemo } from 'react';
import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import { ApisContext } from './ApisContext';
import { DefaultEnvContext } from '../../components/pages/environments/default-env-context/DefaultEnvContext';

export type SweepApi = Pick<AxiosInstance, 'get' | 'post' | 'put' | 'patch' | 'delete'>;

const mergeEnvHeader = (
  config: AxiosRequestConfig | undefined,
  crmOrgId: string | null | undefined,
): AxiosRequestConfig => {
  if (!crmOrgId) return config ?? {};
  return {
    ...config,
    headers: {
      currentcrmorgid: crmOrgId,
      ...config?.headers,
    },
  };
};

const createScopedApi = (axios: AxiosInstance, crmOrgId: string | undefined): SweepApi => ({
  get: (url, config) => axios.get(url, mergeEnvHeader(config, crmOrgId)),
  post: (url, data, config) => axios.post(url, data, mergeEnvHeader(config, crmOrgId)),
  put: (url, data, config) => axios.put(url, data, mergeEnvHeader(config, crmOrgId)),
  patch: (url, data, config) => axios.patch(url, data, mergeEnvHeader(config, crmOrgId)),
  delete: (url, config) => axios.delete(url, mergeEnvHeader(config, crmOrgId)),
});

export const crmOrgHeader = (crmOrgId: string | null): AxiosRequestConfig => ({
  headers: { currentcrmorgid: crmOrgId },
});

export const useSweepApi = (): SweepApi => {
  const { dashboardAPI } = useContext(ApisContext);
  const envCtx = useContext(DefaultEnvContext);

  return useMemo(
    () => createScopedApi(dashboardAPI, envCtx?.crmOrgId),
    [dashboardAPI, envCtx?.crmOrgId],
  );
};
